(() => {
  const root = document.querySelector(".fg-product-main");
  if (!root) return;

  const form = root.querySelector("[data-product-form]");
  if (!form) return;

  const variantsEl = root.querySelector("[data-fg-variants]");
  const pricingEl = root.querySelector("[data-fg-variant-pricing]");
  const variants = variantsEl ? JSON.parse(variantsEl.textContent || "[]") : [];
  const pricingMap = pricingEl ? JSON.parse(pricingEl.textContent || "{}") : {};

  const idInput = form.querySelector('input[name="id"]');
  const addBtn = form.querySelector('button[name="add"]');
  const btnTextEl = addBtn ? addBtn.querySelector("[data-fg-btn-text]") : null;
  const ctaPriceEl = addBtn
    ? addBtn.querySelector("[data-fg-cta-price]")
    : null;

  const purchaseWrap = root.querySelector("[data-fg-purchase]");
  const subscribeRow = root.querySelector("[data-fg-subscribe-row]");
  const sellingPlanSelect = root.querySelector("[data-fg-selling-plan]");
  const pctEls = root.querySelectorAll("[data-fg-max-sub-pct]");

  const onetimePriceEl =
    root.querySelector("[data-fg-onetime-price]") ||
    root.querySelector("[data-fg-plan-price-one]");

  const subPriceEl =
    root.querySelector("[data-fg-subscribe-price]") ||
    root.querySelector("[data-fg-plan-price-sub]");
  const subCompareEl = root.querySelector("[data-fg-subscribe-compare]");
  const subBadgeEl = root.querySelector("[data-fg-subscribe-badge]");
  const subSublineEl = root.querySelector("[data-fg-subscribe-subline]");

  const priceCurrentEl = root.querySelector("[data-fg-price-current]");
  const priceCompareEl = root.querySelector("[data-fg-price-compare]");
  const badgeSaleEl = root.querySelector("[data-fg-badge-sale]");
  const badgePercentEl = root.querySelector("[data-fg-badge-percent]");
  const badgeSoldOutEl = root.querySelector("[data-fg-badge-soldout]");
  const subnoteEl = root.querySelector("[data-fg-subnote]");

  // Note: Shopify's `image_tag` often outputs `srcset`. If we only change `src`,
  // the browser will keep using the old `srcset` candidate. We therefore update
  // (or remove) `srcset` as well when swapping.
  const getMainImg = () =>
    root.querySelector("[data-fg-main-image], .fg-media-img") ||
    root.querySelector(".fg-media-wrap img");
  const thumbsWrap = root.querySelector("[data-fg-thumbs]");
  const prevBtn = root.querySelector("[data-fg-gallery-prev]");
  const nextBtn = root.querySelector("[data-fg-gallery-next]");

  let activeIndex = 0;

  // ---------- Helpers ----------
  function setHidden(el, hidden) {
    if (!el) return;
    el.hidden = !!hidden;
  }

  function formatMoney(cents) {
    if (
      window.Shopify &&
      typeof Shopify.formatMoney === "function" &&
      Shopify.money_format
    ) {
      try {
        return Shopify.formatMoney(cents, Shopify.money_format);
      } catch (_) {}
    }

    const value = Number(cents) / 100;
    const currency = window.Shopify?.currency?.active || "USD";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        currencyDisplay: "symbol",
      }).format(value);
    } catch (_) {
      return `$${value.toFixed(2)}`;
    }
  }

  function getSelectedOptions() {
    const data = new FormData(form);
    const options = [];
    for (const [k, v] of data.entries()) {
      if (k.startsWith("options[")) options.push(v);
    }
    return options;
  }

  function findMatchingVariant() {
    if (!variants.length) return null;
    const selected = getSelectedOptions();

    if (!selected.length) return variants[0];

    return (
      variants.find(
        (v) =>
          Array.isArray(v.options) &&
          v.options.length === selected.length &&
          v.options.every((opt, i) => opt === selected[i]),
      ) || variants[0]
    );
  }

  function isSubscribeMode() {
    if (!purchaseWrap) return false;
    const checked = purchaseWrap.querySelector(
      'input[name="fg_purchase_mode"]:checked',
    );
    return checked && checked.value === "subscribe";
  }

  function getSelectedSellingPlanId() {
    if (!sellingPlanSelect) return null;
    return sellingPlanSelect.value || null;
  }

  function getFirstSellingPlanId() {
    if (!sellingPlanSelect || !sellingPlanSelect.options.length) return null;
    return sellingPlanSelect.options[0].value;
  }

  function getSellingPlanNameFromOption() {
    if (!sellingPlanSelect) return "";
    const opt =
      sellingPlanSelect.selectedOptions && sellingPlanSelect.selectedOptions[0];
    if (!opt) return "";
    return opt.getAttribute("data-fg-sp-name") || opt.textContent.trim();
  }

  function getPricingInfo(variantId) {
    return pricingMap[String(variantId)] || null;
  }

  function calcPctOff(baseCents, subCents) {
    if (!baseCents || subCents == null) return 0;
    if (subCents >= baseCents) return 0;
    return Math.round(((baseCents - subCents) * 100) / baseCents);
  }

  function getSubscriptionAllocation(variantInfo, sellingPlanId) {
    if (!variantInfo || !variantInfo.allocations) return null;
    if (!sellingPlanId) return null;
    return variantInfo.allocations[String(sellingPlanId)] || null;
  }

  function getSubscriptionPriceForVariant(variantId) {
    const info = getPricingInfo(variantId);
    if (!info) return null;

    let spId = getSelectedSellingPlanId() || getFirstSellingPlanId();
    const alloc = getSubscriptionAllocation(info, spId);
    if (!alloc || typeof alloc.price !== "number") return null;

    return {
      spId,
      price: alloc.price,
      compareAt:
        typeof alloc.compare_at_price === "number" ? alloc.compare_at_price : 0,
      basePrice: info.price,
    };
  }

  function getEffectivePriceCents(variantId) {
    const info = getPricingInfo(variantId);
    if (!info) return null;

    if (!isSubscribeMode()) return info.price;

    const sub = getSubscriptionPriceForVariant(variantId);
    return sub ? sub.price : info.price;
  }

  function updateCTAChip(variantId) {
    if (!ctaPriceEl) return;
    const cents = getEffectivePriceCents(variantId);
    if (cents == null) return;
    ctaPriceEl.textContent = formatMoney(cents);
  }

  function updateButtonLabel() {
    if (!addBtn) return;
    const one = addBtn.dataset.labelOnetime || "Add to cart";
    const sub = addBtn.dataset.labelSubscribe || "Sign up now";
    if (btnTextEl) btnTextEl.textContent = isSubscribeMode() ? sub : one;
    else addBtn.textContent = isSubscribeMode() ? sub : one;
  }

  function updateMaxPct(variantId) {
    const info = getPricingInfo(variantId);
    const pct =
      info && typeof info.max_sub_pct === "number" ? info.max_sub_pct : 0;

    pctEls.forEach((el) => (el.textContent = String(pct)));

    if (subBadgeEl) {
      subBadgeEl.textContent = `SAVE ${pct}%`;
    }
  }

  function buildSellingPlanOptionLabels(variantId) {
    if (!sellingPlanSelect) return;
    const info = getPricingInfo(variantId);
    if (!info || !info.allocations) return;

    const base = info.price;

    Array.from(sellingPlanSelect.options).forEach((opt) => {
      const spId = opt.value;
      const alloc = info.allocations[String(spId)];
      const spName =
        opt.getAttribute("data-fg-sp-name") || opt.textContent.trim();

      if (!alloc || typeof alloc.price !== "number") {
        opt.textContent = spName;
        return;
      }

      const pctOff = calcPctOff(base, alloc.price);
      opt.textContent = `${spName} – ${formatMoney(alloc.price)}${pctOff ? ` (${pctOff}% off)` : ""}`;
    });
  }

  function updateCards(variantId) {
    const info = getPricingInfo(variantId);
    if (!info) return;

    if (onetimePriceEl) onetimePriceEl.textContent = formatMoney(info.price);

    const sub = getSubscriptionPriceForVariant(variantId);

    if (subPriceEl) {
      subPriceEl.textContent = formatMoney(sub ? sub.price : info.price);
    }

    if (subCompareEl) {
      const compareAt = sub ? sub.compareAt || 0 : 0;
      const showCompare = compareAt && sub && compareAt > sub.price;
      subCompareEl.textContent = showCompare ? formatMoney(compareAt) : "";
      setHidden(subCompareEl, !showCompare);
    }

    if (subSublineEl) {
      const name = getSellingPlanNameFromOption();

      const cleanName = name;
      subSublineEl.textContent = cleanName ? cleanName : "Delivery every month";
    }

    if (subBadgeEl) {
      const pct = sub ? calcPctOff(sub.basePrice, sub.price) : 0;
      subBadgeEl.textContent = pct ? `SAVE ${pct}%` : "";
      setHidden(subBadgeEl, !pct);
    }

    if (badgeSoldOutEl) setHidden(badgeSoldOutEl, !!info.available);
  }

  function updateMainPriceSnippet(variantId) {
    const info = getPricingInfo(variantId);
    if (!info) return;

    let price = info.price;
    let compareAt = info.compare_at_price || 0;

    const sub = isSubscribeMode()
      ? getSubscriptionPriceForVariant(variantId)
      : null;
    if (sub) {
      price = sub.price;
      compareAt = sub.compareAt || 0;
      if (subnoteEl) setHidden(subnoteEl, false);
    } else {
      if (subnoteEl) setHidden(subnoteEl, true);
    }

    const onSale = compareAt && compareAt > price;
    const pctOff = onSale ? calcPctOff(compareAt, price) : 0;

    if (priceCurrentEl) priceCurrentEl.textContent = formatMoney(price);

    if (priceCompareEl) {
      priceCompareEl.textContent = onSale ? formatMoney(compareAt) : "";
      setHidden(priceCompareEl, !onSale);
    }

    if (badgeSaleEl) setHidden(badgeSaleEl, true);
    if (badgePercentEl) setHidden(badgePercentEl, true);
  }

  function syncPurchaseUI(variantId) {
    if (subscribeRow) subscribeRow.hidden = !isSubscribeMode();

    if (sellingPlanSelect) sellingPlanSelect.disabled = !isSubscribeMode();

    updateButtonLabel();

    buildSellingPlanOptionLabels(variantId);

    updateCards(variantId);
    updateMainPriceSnippet(variantId);
    updateCTAChip(variantId);
  }

  function getToastHost() {
    return (
      root.querySelector("[data-fg-price-row]") ||
      root.querySelector(".fg-info") ||
      root
    );
  }

  function showToast(message) {
    const host = getToastHost();
    const existing = host.querySelector(".fg-toast");
    if (existing) existing.remove();

    const toast = document.createElement("div");
    toast.className = "fg-toast";
    toast.textContent = message;

    host.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add("is-on");
    });

    setTimeout(() => {
      toast.classList.remove("is-on");
      toast.classList.add("is-off");

      setTimeout(() => {
        toast.remove();
      }, 250);
    }, 1600);
  }

  const qtyInput = form.querySelector(".fg-qty-input");
  root.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-fg-qty]");
    if (!btn || !qtyInput) return;
    const current = parseInt(qtyInput.value || "1", 10);
    const next =
      btn.dataset.fgQty === "plus" ? current + 1 : Math.max(1, current - 1);
    qtyInput.value = String(next);
  });

  // ---------- Gallery ----------
  function getThumbButtons() {
    if (!thumbsWrap) return [];
    // Support both data attr and class selector to be resilient to markup changes.
    return Array.from(
      thumbsWrap.querySelectorAll("[data-fg-thumb], .fg-thumb"),
    );
  }

  function setActiveThumb(index) {
    const thumbs = getThumbButtons();
    thumbs.forEach((b) => b.classList.remove("is-active"));
    if (thumbs[index]) thumbs[index].classList.add("is-active");
  }

  function swapMainImageByIndex(index) {
    const thumbs = getThumbButtons();
    const mainImg = getMainImg();
    if (!mainImg || !thumbs.length) return;

    const btn = thumbs[index];
    if (!btn) return;

    const srcFromData = btn.getAttribute("data-fg-src");
    const img = btn.querySelector("img");
    const src = srcFromData || (img ? img.src : null);
    if (!src) return;

    // Try to swap to a larger size if using Shopify img URLs
    const nextSrc = src.replace(/width=\d+/g, "width=1400");

    // Update both src + srcset to ensure the visible image changes.
    mainImg.setAttribute("src", nextSrc);
    // If the theme used srcset, clear it so browser uses our src.
    if (mainImg.hasAttribute("srcset")) mainImg.removeAttribute("srcset");
    if (mainImg.hasAttribute("sizes")) mainImg.removeAttribute("sizes");
    if (mainImg.hasAttribute("data-srcset"))
      mainImg.removeAttribute("data-srcset");
    if (mainImg.hasAttribute("data-sizes"))
      mainImg.removeAttribute("data-sizes");
    if (mainImg.hasAttribute("data-src")) mainImg.removeAttribute("data-src");

    // Some lazyload libs cache currentSrc; forcing a decode helps on a few themes.
    try {
      mainImg.decode && mainImg.decode();
    } catch (_) {}

    activeIndex = index;
    setActiveThumb(activeIndex);
  }

  if (thumbsWrap) {
    thumbsWrap.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-fg-thumb], .fg-thumb");
      if (!btn) return;
      const thumbs = getThumbButtons();
      const idx = thumbs.indexOf(btn);
      if (idx >= 0) swapMainImageByIndex(idx);
    });

    thumbsWrap.addEventListener("keydown", (e) => {
      const thumbs = getThumbButtons();
      if (!thumbs.length) return;
      const focused = document.activeElement;
      const idx = thumbs.indexOf(focused);
      if (idx < 0) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        const next = (idx + 1) % thumbs.length;
        swapMainImageByIndex(next);
        thumbs[next].focus();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        const prev = (idx - 1 + thumbs.length) % thumbs.length;
        swapMainImageByIndex(prev);
        thumbs[prev].focus();
      }
    });
  }

  function nextImage() {
    const thumbs = getThumbButtons();
    if (!thumbs.length) return;
    const next = (activeIndex + 1) % thumbs.length;
    swapMainImageByIndex(next);
  }

  function prevImage() {
    const thumbs = getThumbButtons();
    if (!thumbs.length) return;
    const prev = (activeIndex - 1 + thumbs.length) % thumbs.length;
    swapMainImageByIndex(prev);
  }

  if (nextBtn)
    nextBtn.addEventListener("click", (e) => {
      e.preventDefault();
      nextImage();
    });
  if (prevBtn)
    prevBtn.addEventListener("click", (e) => {
      e.preventDefault();
      prevImage();
    });

  // ---------- Event bindings ----------
  // Purchase toggle
  if (purchaseWrap) {
    purchaseWrap.addEventListener("change", (e) => {
      if (e.target.name !== "fg_purchase_mode") return;
      const v = findMatchingVariant();
      if (!v) return;
      syncPurchaseUI(v.id);
    });
  }

  // Selling plan change
  if (sellingPlanSelect) {
    sellingPlanSelect.addEventListener("change", () => {
      const v = findMatchingVariant();
      if (!v) return;
      // update cards/subline/badges/cta
      syncPurchaseUI(v.id);
    });
  }

  // Variant option changes
  root.addEventListener("change", (e) => {
    if (!e.target.closest("[data-fg-option]")) return;
    const v = findMatchingVariant();
    if (!v) return;
    if (idInput) idInput.value = v.id;
    // keep current thumb index sane
    updateMaxPct(v.id);
    syncPurchaseUI(v.id);
  });

  // AJAX add to cart (stay on product page)
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!addBtn || addBtn.disabled) return;

    const originalLabel = btnTextEl
      ? btnTextEl.textContent
      : addBtn.textContent;
    addBtn.disabled = true;
    if (btnTextEl) btnTextEl.textContent = "Adding...";
    else addBtn.textContent = "Adding...";

    try {
      const fd = new FormData(form);
      const v = findMatchingVariant();
      const variantId = v ? v.id : idInput ? idInput.value : null;

      if (isSubscribeMode()) {
        // Even if select is disabled, ensure selling_plan is sent
        const spId = getSelectedSellingPlanId() || getFirstSellingPlanId();
        if (spId) fd.set("selling_plan", spId);
      } else {
        fd.delete("selling_plan");
      }

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || "Failed to add to cart");
      }

      // Refresh cart + notify header
      try {
        const cartRes = await fetch("/cart.js", {
          headers: { Accept: "application/json" },
        });
        const cart = await cartRes.json();
        if (window.FG_updateCartCount) window.FG_updateCartCount(cart);
        window.dispatchEvent(
          new CustomEvent("fg:cart-updated", { detail: { cart } }),
        );
      } catch (_) {}

      showToast(
        isSubscribeMode() ? "Subscription added to cart" : "Added to cart",
      );

      // keep UI in sync after add
      if (variantId) {
        syncPurchaseUI(variantId);
      }
    } catch (err) {
      alert(err.message || "Something went wrong.");
    } finally {
      addBtn.disabled = false;
      if (btnTextEl) btnTextEl.textContent = originalLabel;
      else addBtn.textContent = originalLabel;
    }
  });

  // ---------- Init ----------
  const initVariant = findMatchingVariant() || variants[0];
  if (initVariant) {
    if (idInput) idInput.value = initVariant.id;
    updateMaxPct(initVariant.id);

    // Ensure selling plan labels show pricing right away
    buildSellingPlanOptionLabels(initVariant.id);

    // Default mode: whatever is checked in markup (you want One-time default)
    if (sellingPlanSelect) sellingPlanSelect.disabled = !isSubscribeMode();
    if (subscribeRow) subscribeRow.hidden = !isSubscribeMode();

    updateButtonLabel();
    updateCards(initVariant.id);
    updateMainPriceSnippet(initVariant.id);
    updateCTAChip(initVariant.id);

    // Gallery init
    const thumbs = getThumbButtons();
    if (thumbs.length) {
      activeIndex = thumbs.findIndex((t) => t.classList.contains("is-active"));
      if (activeIndex < 0) activeIndex = 0;
      setActiveThumb(activeIndex);
    }
  }
})();
